/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * ONE RECONCILED CASE STATE (v1_747).
 *
 * Every officer-facing surface used to read the RAW model fields on a case
 * (report.documentGaps, report.sowAssessment.score, report.activityProfile.score,
 * report.ubos / ownershipChain, the raw sumsub_verifications rows) and each
 * reconciled the officer's decisions differently, or not at all. So the Review
 * report sent to a second signatory could read four document rows Outstanding,
 * Source of wealth Insufficient and 3 of 3 individuals verified while the board,
 * the sign off dialog and the rationale all read zero outstanding and two
 * parties (CompliantID f8d452f3).
 *
 * This is the single deterministic reconciliation. It takes the whole session
 * (report, collected_data, the document rows, the sumsub verifications, the AI
 * review, wallet screenings, the decision record) and returns ONE reconciled
 * view: open items, the blocking decision conditions, the noted facts, every
 * check state with the officer's resolution applied, the source of wealth / funds
 * acceptance, the screening verdict, the reconciled ownership (chain legs merged,
 * corporate holders as intermediaries), and the verification party set with each
 * party's status. Every consumer reads THIS; none re-reads the raw fields.
 *
 * Pure, no I/O, so it is shared by the dashboard, the Review report, the exports,
 * the Jaybee report/review, the officer-side Alex context and the harness alike.
 */
import { tierInForce } from './tier-in-force.js';
import { isIndividualCase } from './individual-case.js';
import { identityStatusOf, identityRank, SCREENED_ONLY, SCREENED_ONLY_WORDS } from './identity-method.js';
import { individualDecisionChecks, lockUnresolvable } from './individual-checks.js';
import {
  applyCheckResolutions, reconciledOpenItems, materialDocuments,
  amlPolicyRequired, amlPolicyDone, blockingOutstanding, notedFacts,
  sowSofAcceptance, acceptedAssessmentScore,
} from './board-checks.js';
import { caseOwnershipThreshold, atOrAboveThreshold } from './ownership-threshold.js';
import { walletRow, resolutionCovers, walletBlockerLabel } from './wallet-screening.js';
import { partyScreeningRows, sanctionsRowState } from './party-screening.js';
import { resolveVerificationParties, isVerificationListEntry, applicantNamesFromBoard } from './verification-parties.js';
import { fileMatcher } from './person-aliases.js';
import { reconcileOwnership } from './ownership-reconcile.js';
import { samePerson as defaultSamePerson } from './person-name-match.js';
import { mentionsUnnegated } from './negation.js';
import { sowNewInterviewPending, sowDay } from './sow-links.js';
import { notReadOpen } from './doc-not-read.js';

const clean = (v) => String(v == null ? '' : v).trim();
const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(v));

// The entity sanctions record on collected_data is stored in the raw screen
// shape (sanctioned / hasPartialMatches / dismissed). The dashboard normalises it
// to { status, data } before any consumer reads it (MLRODashboard loadCaseState).
// Do the same here so screening.entity.status is the SAME string every surface
// reads, never the raw boolean shape.
export function normaliseEntitySanctions(stored, features = {}) {
  if (!(features.sanctions && stored && !stored.error && !stored.keyRequired)) return null;
  const dismissed = stored.dismissed || {};
  const all = [...(stored.hits || []), ...(stored.cautionMatches || [])];
  const allDismissed = all.length > 0 && all.every((h) => dismissed[h && h.name]);
  if (allDismissed) return { status: 'clear_with_notes', data: stored, checkedAt: stored.checkedAt || stored.timestamp || null };
  if (stored.sanctioned) return { status: 'hit', data: stored, checkedAt: stored.checkedAt || stored.timestamp || null };
  if (stored.hasPartialMatches) return { status: 'caution', data: stored, checkedAt: stored.checkedAt || stored.timestamp || null };
  return { status: 'clear', data: stored, checkedAt: stored.checkedAt || stored.timestamp || null };
}

// Verification status for one party against the sumsub verification map, by the
// same name/email match the dashboard uses (rank the best status when a person
// appears more than once). Pure: the matcher is injected (defaults to the shared
// samePerson) so it is identical to the dashboard's resolveVerification.
function verificationFor(map, name, email, match, origin) {
  if (!map) return undefined;
  const rank = { approved: 5, approved_with_flags: 4, requires_review: 3, rejected: 3, in_progress: 2, link_generated: 1 };
  if (name) {
    // v1_811: the exact name key is ranked with its variants, never returned
    // first, so a stale link row under the exact name cannot hide an approved
    // row under another spelling of the same person.
    let best; let bestRank = -1;
    for (const key of Object.keys(map)) {
      if (key !== name && !match(key, name)) continue;
      const r = map[key] ? identityRank(map[key], { origin }) : 0;
      if (r > bestRank) { best = map[key]; bestRank = r; }
    }
    if (best) return best;
  }
  if (looksLikeEmail(email)) {
    const want = clean(email).toLowerCase();
    const hits = Object.keys(map).filter((k) => clean(map[k] && map[k].person_email).toLowerCase() === want);
    if (hits.length === 1) return map[hits[0]];
  }
  return undefined;
}

// Merge ownership legs so a holding declared on the file AND asserted by a
// register does not appear twice (the "Creative Agentics Limited leg listed
// twice" bug in the flow map). Key on the normalised (holder, held) pair; a
// register-sourced (verified) leg wins, and a numeric percentage beats a blank or
// non-numeric one. Order preserved by first appearance. The flow map calls this
// on its declared+registry legs before it emits node evidence and edges.
export function dedupeOwnershipLegs(legs = []) {
  // Strip a parenthetical aside ("CompliantID (COMPLIANTID LIMITED)") and the
  // legal-form suffix, so the applicant named two ways, or a company written with
  // and without "Limited", is one node and its leg is not listed twice.
  const norm = (v) => clean(v).replace(/\([^)]*\)/g, ' ').toLowerCase().replace(/[.,]/g, '')
    .replace(/\b(limited|ltd|plc|llp|llc|inc|corp|corporation|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|pte|pty)\b/g, '').replace(/\s+/g, ' ').trim();
  const pct = (p) => { const m = String(p == null ? '' : p).match(/(\d+(?:\.\d+)?)/); return m ? Number(m[1]) : null; };
  const isReg = (l) => /register|extract|kyckr|companies house|psc/i.test(String(l && l.source || ''));
  const byKey = new Map();
  const order = [];
  for (const l of (Array.isArray(legs) ? legs : [])) {
    if (!l || !clean(l.holder) || !clean(l.held)) continue;
    const key = norm(l.holder) + '>' + norm(l.held);
    const cur = byKey.get(key);
    if (!cur) { byKey.set(key, { ...l }); order.push(key); continue; }
    // Prefer a register-sourced leg; otherwise fill a blank/non-numeric percentage
    // and a missing source from the later copy.
    if (isReg(l) && !isReg(cur)) { byKey.set(key, { ...cur, ...l }); continue; }
    if (pct(l.percentage) != null && pct(cur.percentage) == null) cur.percentage = l.percentage;
    if (!clean(cur.source) && clean(l.source)) cur.source = l.source;
  }
  return order.map((k) => byKey.get(k));
}

/**
 * Reconcile the whole case into one view.
 *
 * @param session {
 *   report, collected_data, documents, documentTracking, sumsubVerifications,
 *   aiReview, walletScreenings, decisionRecord, mlroDecision, documentAcknowledgments
 * }
 * @param opts { features, samePerson, threshold }
 */
// v1_812: THE IDENTITY VERIFICATION SET, one function for every surface. The
// dashboard header read identityState (the stored identity list plus the
// establishing person) while the case state counted the board's resolved
// parties, so one case read "1 of 3 verified" in one place and "2 / 4 verified"
// in another (Fig Markets, production 25/09/26). Both now read this:
//   the establishing person (the case contact), when not already listed;
//   the stored identity list (report.sumsubRequired), each entry a verification
//     list entry (not a non party, not a sub threshold holder only);
//   the board's resolved parties not already on it;
// deduplicated with the file aware matcher (an alias the file records, an
// officer's Same person as merge), each person matched to their best
// verification row across every name the file links to them, with every other
// row for them kept as their history (an older link is never a second person).
const VERIFIED = ['approved', 'approved_with_flags'];
// origin (29/10/2026): the case's origin column; a clear Screen only result counts
// for identity only on a migrated case (src/shared/identity-method.js identityStatusOf).
export function identityParties({ report, collected_data, sumsubVerifications, contactName, contactEmail, origin } = {}, baseMatch = defaultSamePerson) {
  const r = (report && typeof report === 'object') ? report : {};
  const cd = (collected_data && typeof collected_data === 'object') ? collected_data : {};
  const verifs = sumsubVerifications || {};
  const fm = fileMatcher(r, cd, baseMatch);
  const apps = applicantNamesFromBoard(r);
  const nameOf = (p) => (p && typeof p === 'object') ? p.name : String(p || '').split(' \u2014 ')[0];
  const people = [];
  // The name shown for a merged person: the one on the board (directors, UBOs,
  // signatories) first, so "Steven Brodie" stands over an old row's "Mr Steven
  // Anthony Brodie"; otherwise the fuller spelling.
  const onBoard = ['directors', 'ubos', 'authorisedSignatories'].flatMap((l) => (Array.isArray(r[l]) ? r[l] : []).map(nameOf)).filter(Boolean);
  const isBoardName = (n) => onBoard.some((b) => String(b).trim() === String(n).trim());
  const add = (entry) => {
    const nm = nameOf(entry);
    if (!nm || !String(nm).trim()) return;
    const e = { name: nm, role: (entry && typeof entry === 'object' && entry.role) || '', email: (entry && typeof entry === 'object' && entry.email) || null };
    const at = people.findIndex((x) => fm(x.name, nm));
    if (at === -1) { people.push(e); return; }
    const held = people[at];
    const takeNew = isBoardName(nm) !== isBoardName(held.name) ? isBoardName(nm) : String(nm).length > String(held.name).length;
    if (takeNew) people[at] = { ...held, ...e, email: e.email || held.email, role: held.role || e.role };
    else if (!held.email && e.email) held.email = e.email;
  };
  const listed = Array.isArray(r.sumsubRequired) ? r.sumsubRequired : [];
  const cName = String(contactName || '').trim();
  const cEmail = String(contactEmail || '').trim();
  const emailOk = (x) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(x || '').trim());
  if (cName || emailOk(cEmail)) {
    const already = listed.some((p) => (cName && fm(nameOf(p), cName)) || (emailOk(cEmail) && p && typeof p === 'object' && emailOk(p.email) && String(p.email).trim().toLowerCase() === cEmail.toLowerCase()));
    if (!already) add({ name: cName || cEmail, role: 'Authorised signatory (establishing person)', email: emailOk(cEmail) ? cEmail : null });
  }
  // v1_856: a party the officer removed (collected_data.partyRemovals) is off
  // the set, whichever list still names them. The set rebuilt people from the
  // board's lists, and a removal that left a name on one (an authorised
  // signatory) kept the person on the panel with Send Link. The establishing
  // person (the case contact, above) is never taken off: they must be
  // identified in their own right. Their verification rows stay on record.
  const removedNames = Object.values((cd.partyRemovals && typeof cd.partyRemovals === 'object') ? cd.partyRemovals : {}).filter((x) => x && x.name).map((x) => x.name);
  const removed = (nm) => !!nm && !(cName && fm(nm, cName)) && removedNames.some((rn) => fm(rn, nm));
  for (const p of listed) { const e = (p && typeof p === 'object') ? p : { name: nameOf(p) }; if (isVerificationListEntry(e, apps, caseOwnershipThreshold(r)) && !removed(e.name)) add(e); }
  for (const p of resolveVerificationParties(r, fm)) if (!removed(p.name)) add(p);
  const rows = Object.values(verifs).filter((v) => v && v.person_name);
  const withStatus = people.map((p) => {
    const v = verificationFor(verifs, p.name, p.email, fm, origin);
    const history = rows.filter((x) => x !== v && fm(x.person_name, p.name));
    return { ...p, verification: v || null, status: v ? identityStatusOf(v, { origin }) : '', history };
  });
  const verified = withStatus.filter((p) => VERIFIED.includes(p.status));
  return {
    match: fm,
    people: withStatus,
    total: withStatus.length,
    verified: verified.length,
    pending: withStatus.filter((p) => !p.verification || ['link_generated', 'pending', 'withdrawn', ''].includes(p.status)),
    outstanding: withStatus.filter((p) => !VERIFIED.includes(p.status)),
  };
}

export function reconcileCaseState(session = {}, opts = {}) {
  const r = (session.report && typeof session.report === 'object') ? session.report : {};
  const cd = (session.collected_data && typeof session.collected_data === 'object') ? session.collected_data : {};
  const features = opts.features || cd.firmFeatures || {};
  const match = typeof opts.samePerson === 'function' ? opts.samePerson : defaultSamePerson;
  const documents = Array.isArray(session.documents) ? session.documents : [];
  const documentTracking = Array.isArray(session.documentTracking) ? session.documentTracking : [];
  const sumsubVerifications = session.sumsubVerifications || {};
  const aiReview = session.aiReview || null;
  const walletScreenings = Array.isArray(session.walletScreenings) ? session.walletScreenings : [];
  const decisionRecord = session.decisionRecord || cd.decisionRecord || null;
  const checkRes = cd.checkResolutions || {};
  const acc = sowSofAcceptance(checkRes);

  // ── Screening ─────────────────────────────────────────────────────────────
  const entity = normaliseEntitySanctions(cd.entitySanctions, features);
  const parties = partyScreeningRows(r, cd.partyScreenings, cd.screeningResolutions);
  const screeningRow = sanctionsRowState(entity, parties, { individual: isIndividualCase(cd) });

  // ── Verification parties (the canonical, deduped set) ───────────────────────
  // v1_799: an email the officer set on the identity row (collected_data
  // .partyEmails) stands for a party whose board entry has none, so it survives
  // a rebuild that drops it.
  const partyEmails = (cd.partyEmails && typeof cd.partyEmails === 'object') ? Object.values(cd.partyEmails) : [];
  const officerEmailFor = (name) => { const e = partyEmails.find((x) => x && x.name && x.email && match(x.name, name)); return e ? e.email : null; };
  // v1_812: the one identity set (identityParties), the same the dashboard's
  // header, panel and sign off read.
  const _idp = identityParties({ report: r, collected_data: cd, sumsubVerifications, contactName: session.contactName, contactEmail: session.contactEmail, origin: session.origin }, match);
  const fm = _idp.match;
  const vParties = _idp.people.map((p) => {
    const email = p.email || officerEmailFor(p.name) || null;
    const v = (email && email !== p.email) ? (verificationFor(sumsubVerifications, p.name, email, fm, session.origin) || p.verification) : p.verification;
    return { ...p, email, status: (v && identityStatusOf(v, { origin: session.origin })) || 'not_sent', verification: v || null };
  });
  const partyCount = vParties.length;
  const verifiedCount = vParties.filter((p) => ['approved', 'approved_with_flags'].includes(String(p.status))).length;

  // ── Ownership (chain legs merged; corporate holders as intermediaries) ───────
  const ownRec = reconcileOwnership(r, cd.registerExtracts || [], { threshold: opts.threshold || caseOwnershipThreshold(r) });
  const ownership = {
    ubos: ownRec.resolution ? ownRec.ubos : (Array.isArray(r.ubos) ? r.ubos : []),
    intermediaries: ownRec.resolution ? ownRec.intermediaries : (Array.isArray(r.intermediaries) ? r.intermediaries : []),
    ownershipChain: dedupeOwnershipLegs(ownRec.resolution ? ownRec.ownershipChain : (Array.isArray(r.ownershipChain) ? r.ownershipChain : [])),
    conflict: ownRec.conflict || null,
  };

  // ── Blocking checks (mirror computeDecisionItems), officer resolutions applied ─
  const sofBase = (r.sofAssessment && r.sofAssessment.score || '').split(/[—–-]/)[0].trim().toLowerCase();
  const sofSatisfactory = sofBase === 'satisfactory' || sofBase === 'accepted by mlro';
  const idOk = !!(r.companyDetails && r.companyDetails.legalName && r.companyDetails.registrationNumber);
  const legitBlockers = documents.filter((d) =>
    (d && (d.analysis && (d.analysis.materialFlags || []).length > 0 || (d.analysis && (d.analysis.redFlags || []).length > 0)))
    && !(d && d.mlro_note)
    && !((session.documentAcknowledgments || {})[d && d.file_name])
    && !['Satisfactory', 'Superseded', 'Accepted by MLRO'].includes(d && d.assessment)
    && ['certificate of incorporation', 'registration', 'good standing'].some((kw) => String((d && d.file_name) || '').toLowerCase().includes(kw)));

  const cdet = r.companyDetails || {};
  const rs = r.regulatoryStatus || {};
  const checks = [
    {
      key: 'legit', q: 'Is the company legitimate?', done: idOk && legitBlockers.length === 0,
      detail: !idOk
        ? (cdet.legalName ? `${cdet.legalName} · registration number not on file` : 'Not confirmed')
        : legitBlockers.length
          ? `${cdet.legalName} · unresolved findings on: ${legitBlockers.map((f) => `${f.file_name}${f.assessment ? ' (' + f.assessment + ')' : ' (not yet assessed)'}`).join(', ')}. Open the document and acknowledge its findings with a note, or set its status`
          : `${cdet.legalName} · ${cdet.jurisdiction || ''}`,
    },
    {
      key: 'regulated', q: 'Are they regulated?',
      detail: rs.regulated ? `${rs.regulator}, ${rs.licenceNumber || 'licence on file'}` : rs.licenceType ? rs.licenceType : 'Unregulated, mitigation required',
      done: !!(rs.regulated && rs.regulator) || !!(rs.regulated === false && session.mlroDecision),
    },
    {
      key: 'ubo', q: 'Who is the UBO?',
      detail: (r.ubos || []).map((u) => `${u.name}${u.ownership == null || u.ownership === '' ? ' (holding not stated)' : ' ' + u.ownership + '%'}`).join(', ') || 'Not identified',
      done: (r.ubos || []).length > 0 && (r.ubos || []).every((u) => u && u.name && u.ownership),
    },
    {
      key: 'directors', q: 'Who runs it?',
      detail: (r.directors || []).map((d) => (typeof d === 'string' ? d : d && d.name)).filter(Boolean).join(', ') || 'Not identified',
      done: (r.directors || []).length > 0,
    },
    {
      key: 'money', q: 'How does the company make its money?',
      detail: cdet.businessDescription ? String(cdet.businessDescription).slice(0, 80) : (sofSatisfactory ? 'Documented' : 'Needs further evidence'),
      done: !!(cdet.businessDescription || sofSatisfactory),
    },
    {
      key: 'funds', q: 'Where are funds coming from?',
      detail: (r.sofAssessment && r.sofAssessment.namedAccount) ? `${r.sofAssessment.namedAccount}${r.sofAssessment.fundingRoute ? ' · ' + String(r.sofAssessment.fundingRoute).slice(0, 400) : ''}` : 'Not confirmed',
      done: !!(r.sofAssessment && r.sofAssessment.namedAccount && sofSatisfactory),
    },
  ];
  // Individual onboarding (piece 8): a person's questions in place of the
  // company's (src/shared/individual-checks.js). session.formRequests (the
  // case's Form A/K rows, when the caller read them) and
  // session.individualFormA (the Form A state from the case's regime).
  const _indiv = isIndividualCase(cd);
  if (_indiv) checks.splice(0, checks.length, ...individualDecisionChecks(r, cd, { checkRes, sofSatisfactory, formRequests: session.formRequests, formRequestsPartial: session.formRequestsPartial, formAState: session.individualFormA, features, samePerson: match }));
  if (features.sanctions) {
    checks.push({ key: 'sanctions', q: 'Sanctions and PEP screening (OpenSanctions)', detail: screeningRow.detail, done: screeningRow.done, resolvable: false });
  }
  // ── Intermediate corporate holders (v1_748): entity screen + evidence ───────
  // An intermediate is VERIFIED only when it is screened clear AND its ownership
  // legs carry a registry-class source (screening proves the entity is not
  // sanctioned; it does not prove the entity exists or that it holds what the
  // chart claims). An officer may Resolve the check with a reason instead. The
  // entity screen is the same idempotent helper run on every generation path;
  // its results live in collected_data.intermediaryScreenings.
  const SRC_DOC = /(register|extract|certificate|companies house|registry|filing|annual return|confirmation statement)/i;
  const nm2 = (v) => clean(v).replace(/\([^)]*\)/g, ' ').toLowerCase().replace(/[.,]/g, '')
    .replace(/\b(limited|ltd|plc|llp|llc|inc|corp|corporation|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|pte|pty)\b/g, '').replace(/\s+/g, ' ').trim();
  const interScreens = Array.isArray(cd.intermediaryScreenings) ? cd.intermediaryScreenings : [];
  const legsRegistrySourced = (name) => {
    const legs = ownership.ownershipChain.filter((l) => nm2(l.holder) === nm2(name) || nm2(l.held) === nm2(name));
    return legs.length > 0 && legs.every((l) => SRC_DOC.test(String(l.source || '')));
  };
  const intermediaryScreening = (ownership.intermediaries || []).map((im) => {
    const sc = interScreens.find((s) => s && nm2(s.name) === nm2(im.name)) || null;
    const screenedClear = !!(sc && ['clear', 'clear_with_notes'].includes(sc.status));
    const legsOk = legsRegistrySourced(im.name);
    return { name: im.name, status: sc ? sc.status : 'not_screened', checkedAt: (sc && sc.checkedAt) || null, screenedClear, legsRegistrySourced: legsOk, verified: screenedClear && legsOk };
  });
  // Model tier pending (29/10/2026): the tier in force, never the model's riskTier.
  const _tierNow = tierInForce(r);
  const interReq = !_indiv && (!!(features && features.require_intermediary_screening) || _tierNow === 'High');
  const intermediaryCheck = {
    key: 'intermediary', q: 'Intermediate entity verified', resolvable: true,
    detail: intermediaryScreening.length === 0 ? 'No intermediate corporate holders in the chain'
      : intermediaryScreening.map((x) => `${x.name}: ${x.screenedClear ? 'screened clear' : (x.status === 'not_screened' ? 'not screened' : x.status)}${x.legsRegistrySourced ? ', legs registry-sourced' : ', legs need a registry source or a recorded reason'}`).join('; '),
    done: intermediaryScreening.length === 0 || intermediaryScreening.every((x) => x.verified),
  };
  if (interReq) checks.push(intermediaryCheck);

  const amlReq = !_indiv && amlPolicyRequired(r, { firmMandated: !!(features && features.require_aml_policy) });
  const amlDone = amlPolicyDone(r, checkRes.amlpolicy);
  const amlDetail = amlDone
    ? ((r.amlPolicyAssessment && r.amlPolicyAssessment.score) || (r.amlPolicyAssessment && r.amlPolicyAssessment.provided ? 'On file' : 'Accepted'))
    : ((r.amlPolicyAssessment && r.amlPolicyAssessment.provided === false) ? (amlReq ? 'Required, not provided' : 'Not provided (not required for this entity)') : ((r.amlPolicyAssessment && r.amlPolicyAssessment.score) || 'Not assessed'));
  const amlPolicyCheck = { key: 'amlpolicy', q: 'AML policy', detail: amlDetail, done: amlDone, resolvable: true };
  if (amlReq) checks.push(amlPolicyCheck);
  applyCheckResolutions(checks, checkRes);
  if (_indiv) lockUnresolvable(checks);

  // ── Procedural checks ───────────────────────────────────────────────────────
  const sumsubDone = verifiedCount;
  // 29/10/2026: people with a clear Screen only result and no identity check (a new client).
  const _screenedOnly = vParties.filter((p) => p.status === SCREENED_ONLY).map((p) => p.name);
  // v1_776: a negated mention ("holds no client money and no crypto") is not a
  // crypto indication; it made Wallet screening and Travel Rule required on an
  // adviser with no crypto, and the case could not reach Decide (walk run 14).
  const hasCryptoIndication = !!(
    mentionsUnnegated(String(r.sofAssessment && r.sofAssessment.fundingRoute || '').toLowerCase(), /crypto|wallet|bitcoin|ethereum|usdt|usdc|bnb|solana|tron|blockchain|digital.asset/) ||
    mentionsUnnegated(String(r.sofAssessment && r.sofAssessment.narrative || '').toLowerCase(), /crypto|wallet|bitcoin|ethereum|usdt|usdc|blockchain/) ||
    (cd.walletAddresses || []).length > 0 ||
    walletScreenings.length > 0 ||
    (r.redFlags || []).some((f) => String(f).toLowerCase().includes('wallet')) ||
    (r.travelRule && (r.travelRule.wallets || []).length > 0)
  );
  // v1_830: at or above the threshold the case was built under (the firm's).
  const ownershipThreshold = caseOwnershipThreshold(r);
  const ubos25 = (r.ubos || []).filter((u) => atOrAboveThreshold(u && u.ownership, ownershipThreshold));
  // v1_748: natural-person UBOs whose per-UBO source-of-wealth interview flag is
  // outstanding. This drives the SoW procedural check (below) so the flag is a
  // resolvable board item, not a flow-map amber the board never asked about.
  // v1_897: a new interview the officer asked for keeps the person outstanding
  // until it completes, whatever the previous interview's flags still say.
  const sowFlagged = (r.ubos || []).filter((u) => u && u.corporateUBO !== true && (sowNewInterviewPending(u) || ((u.sowInterviewRequired || u.sowInterviewSent) && !u.sowInterviewComplete && !u.sowSignedOff)));
  // The company-level source of wealth acceptance is offered as the default reason
  // for resolving the per-UBO SoW check.
  const sowDefaultResolveReason = (acc && acc.accepted) ? ('Source of wealth accepted at company level' + (acc.note ? ': ' + acc.note : '')) : '';
  const activityDetail = (!r.activityProfile || (!r.activityProfile.customerTypes && !r.activityProfile.annualTurnover && !r.activityProfile.monthlyTransactionValue)) ? 'Not captured'
    : /insufficient/i.test(r.activityProfile.score || '') ? 'Insufficient: clarify with client'
      : /elevated/i.test(r.activityProfile.score || '') ? 'Captured (elevated, monitor)'
        : 'Captured (satisfactory)';
  const activityDone = !!(r.activityProfile && (r.activityProfile.customerTypes || r.activityProfile.annualTurnover || r.activityProfile.monthlyTransactionValue) && !/insufficient/i.test(r.activityProfile.score || ''));
  // An individual's expected activity: the declared amounts or the profile.
  const _da = (r.declaredAmounts && typeof r.declaredAmounts === 'object') ? r.declaredAmounts : {};
  const _ap = (r.activityProfile && typeof r.activityProfile === 'object') ? r.activityProfile : {};
  const _indivActivity = !!(_ap.expectedActivity || _ap.monthlyTransactionValue || _da.expectedFirst12MonthsAmount || _da.expectedFirst12Months) && !/insufficient/i.test(_ap.score || '');
  const _indivActivityDetail = _indivActivity ? [_da.expectedFirst12Months || (_da.expectedFirst12MonthsAmount ? `${_da.currency || ''} ${_da.expectedFirst12MonthsAmount} in the first twelve months`.trim() : ''), _da.frequency, _ap.expectedActivity].filter(Boolean).join(' · ').slice(0, 300) || 'Captured' : /insufficient/i.test(_ap.score || '') ? 'Insufficient: clarify with client' : 'Not captured';
  const procedural = [
    ...(features.sumsub !== false ? [{ key: 'sumsub', q: 'Identity verified (Sumsub)', detail: partyCount > 0 ? `${sumsubDone} of ${partyCount} verified` + (_screenedOnly.length ? `; ${_screenedOnly.join(', ')} ${_screenedOnly.length === 1 ? 'is' : 'are'} ${SCREENED_ONLY_WORDS}` : '') : 'No verifications required', done: partyCount === 0 || sumsubDone === partyCount }] : []),
    // v1_825: blocks sign off only with an address on file and unscreened
    // (resolvable with a reason); with no address it reads "No address on file,
    // screen at first transfer" and does not block.
    ...(features.chainalysis ? [walletRow({ collectedData: cd, report: r, walletScreenings, hasCryptoIndication })] : []),
    ...(features.travel_rule ? [{ key: 'travelrule', q: 'Travel Rule', detail: (r.travelRule && r.travelRule.overallStatus && r.travelRule.overallStatus !== 'Not assessed') ? r.travelRule.overallStatus : !hasCryptoIndication ? 'Not applicable, no crypto wallets declared' : 'Not assessed', done: !!(r.travelRule && r.travelRule.overallStatus && r.travelRule.overallStatus !== 'Not assessed') || !hasCryptoIndication }] : []),
    ...(features.sow_interviews && !_indiv ? [{
      key: 'sow', q: 'UBO Source of Wealth interview', resolvable: true,
      // v1_748: an outstanding per-UBO interview flag keeps this OPEN (resolvable
      // with a reason) at any tier, so the flow-map "interview outstanding" node is
      // a board item the officer answers; the company SoW acceptance is the default
      // reason. High tier additionally needs every UBO at or above the case's
      // ownership threshold (v1_830) complete.
      detail: sowFlagged.length ? sowFlagged.map((u) => sowNewInterviewPending(u) ? `${u.name}: new interview requested${sowDay(u.sowNewInterviewRequestedAt) ? ' on ' + sowDay(u.sowNewInterviewRequestedAt) : ''}, awaiting the person` : `${u.name}: interview outstanding`).join(', ')
        : (_tierNow === null) ? 'Not decided yet, the risk assessment has not completed (Assessment pending). It can be sent manually.'
        : (_tierNow !== 'High') ? 'Not required automatically, the client is not high risk (it can be sent manually)'
          : ubos25.length ? ubos25.map((u) => ((u.sowInterviewComplete || u.sowSignedOff) && !sowNewInterviewPending(u)) ? `${u.name} ✓` : `${u.name}: pending`).join(', ') : `No UBOs at or above ${ownershipThreshold} percent`,
      // Review (30/10/2026): while no tier is in force the row is not done; it
      // reads "Not decided yet", so it must not count as passed either.
      done: sowFlagged.length === 0 && _tierNow !== null && ((_tierNow !== 'High') || !ubos25.length || ubos25.every((u) => (u.sowInterviewComplete || u.sowSignedOff) && !sowNewInterviewPending(u))),
    }] : []),
    _indiv ? { key: 'activity', q: 'Expected activity', detail: _indivActivityDetail, done: _indivActivity, resolvable: true } : { key: 'activity', q: 'Customer base & anticipated activity', detail: activityDetail, done: activityDone, resolvable: true },
    ...(!amlReq && !_indiv ? [amlPolicyCheck] : []),
    ...(!interReq && !_indiv ? [intermediaryCheck] : []),
  ];
  // v1_825: a wallet resolution covers the addresses open when it was given; a
  // later unscreened address opens the row again.
  const _walletRow = procedural.find((c) => c.key === 'wallets');
  const procRes = (_walletRow && checkRes && checkRes.wallets && !resolutionCovers(checkRes.wallets, _walletRow.unscreened))
    ? Object.fromEntries(Object.entries(checkRes).filter(([k]) => k !== 'wallets')) : checkRes;
  applyCheckResolutions(procedural, procRes);
  if (_indiv) lockUnresolvable(procedural);

  // ── Exceptions, open items, blocking, noted ─────────────────────────────────
  const trackedStatus = (gapKey) => {
    const t = documentTracking.find((x) => x && (x.item_name === gapKey));
    return (t && t.status) || 'outstanding';
  };
  const openItems = reconciledOpenItems({
    requestItems: cd.requestItems || [],
    documentGaps: r.documentGaps || [],
    docStatusOf: (k) => trackedStatus(k),
  });
  const materialDocs = materialDocuments(documents);
  const openRedLines = (aiReview && aiReview.redLines || []).filter((rl) => rl && (typeof rl === 'string' || !rl.resolved));
  const exceptions = [];
  if (!aiReview) exceptions.push({ key: 'ai', severity: 'blocker', label: 'AI Intelligence Review not yet run' });
  if (openRedLines.length > 0) exceptions.push({ key: 'redlines', severity: 'blocker', label: `${openRedLines.length} open red line${openRedLines.length > 1 ? 's' : ''} in the AI review` });
  if (materialDocs.length > 0) exceptions.push({ key: 'docs', severity: 'action', label: `${materialDocs.length} document${materialDocs.length > 1 ? 's' : ''} marked Requires Clarification, not yet accepted by the MLRO` });
  if (openItems.length > 0) exceptions.push({ key: 'gaps', severity: 'action', label: `${openItems.length} item${openItems.length > 1 ? 's' : ''} outstanding with the client` });
  // UPLOAD FIX BATCH (fault g, owner 15/10/2026): a document that was not read
  // blocks approval until it is read, accepted by the MLRO or superseded. A blocker
  // here (the board is not ready for sign off); the sign off gate refuses the approval
  // outright, with no override (src/shared/signoff-gate.js, unread_documents).
  const notReadDocs = notReadOpen(documents);
  if (notReadDocs.length > 0) exceptions.push({ key: 'notread', severity: 'blocker', label: `${notReadDocs.length} document${notReadDocs.length > 1 ? 's' : ''} not read` });
  // v1_825: an address on file and unscreened, not resolved, blocks sign off.
  if (_walletRow && _walletRow.blocking && !_walletRow.done) exceptions.push({ key: 'wallets', severity: 'blocker', label: walletBlockerLabel(_walletRow.unscreened.length) });

  // v1_913b (owner, 04/10/2026): AN UNVERIFIED IDENTITY CHECK OR A DOCUMENT
  // NEEDING CLARIFICATION IS OPEN AT THE DECISION, NOT A NOTED FACT. Noted facts
  // never ask for an override (v1_913), and these two were listed among them, so
  // approving over an unverified identity or a document still marked Requires
  // Clarification asked for no override. They are now openForOverride: the sign
  // off gate counts them as open (an approval over them needs a recorded
  // override), and the rationale still states them. Noted keeps the client
  // items outstanding (for example a register of members) and the other
  // procedural steps that complete after approval.
  const identityOpen = procedural.filter((c) => c && c.key === 'sumsub' && !c.done);
  const openForOverride = notedFacts({ materialDocs, openItemsCount: 0, procedural: identityOpen });
  const noted = notedFacts({ materialDocs: [], openItemsCount: openItems.length, procedural: procedural.filter((c) => !(c && c.key === 'sumsub')) });
  const reconciledOutstanding = blockingOutstanding(checks, exceptions);
  const blockerCount = exceptions.filter((e) => e.severity === 'blocker').length;
  const readyForSignoff = checks.every((c) => c.done) && blockerCount === 0;

  // ── Reconciled per-gap document status (for the exports / audit record) ──────
  const documentGaps = (r.documentGaps || []).map((gap) => {
    const gapKey = typeof gap === 'string' ? gap : (gap.documentType || gap.description || gap.gap || JSON.stringify(gap));
    const tk = documentTracking.find((x) => x && x.item_name === gapKey) || {};
    if (openItems.length === 0) return { gap: gapKey, status: tk.status && tk.status !== 'outstanding' ? tk.status : 'resolved', reason: tk.waiver_reason || 'Reconciled: the request item covering this gap is resolved' };
    return { gap: gapKey, status: tk.status || 'outstanding', reason: tk.waiver_reason || null };
  });

  // ── Officer resolutions and acceptances (for the audit / case file section) ──
  const officerResolutions = Object.keys(checkRes || {}).map((key) => {
    const chk = [...checks, ...procedural].find((c) => c.key === key) || {};
    return { key, by: checkRes[key].by || '', at: checkRes[key].at || '', note: checkRes[key].note || '', question: chk.q || key, modelAssessment: chk.detail || '' };
  });

  // ── Source of wealth / funds, officer acceptance first, model beneath ────────
  const sow = {
    accepted: !!(acc && acc.accepted), by: acc && acc.by || '', at: acc && acc.at || '', note: acc && acc.note || '',
    modelScore: (r.sowAssessment && r.sowAssessment.score) || null,
    displayScore: acceptedAssessmentScore(r.sowAssessment && r.sowAssessment.score, acc),
    displayScoreWithReason: acceptedAssessmentScore(r.sowAssessment && r.sowAssessment.score, acc, { withReason: true }),
    narrative: (r.sowAssessment && r.sowAssessment.narrative) || '',
  };
  const sof = {
    accepted: !!(acc && acc.accepted), by: acc && acc.by || '', at: acc && acc.at || '', note: acc && acc.note || '',
    modelScore: (r.sofAssessment && r.sofAssessment.score) || null,
    displayScore: acceptedAssessmentScore(r.sofAssessment && r.sofAssessment.score, acc),
    displayScoreWithReason: acceptedAssessmentScore(r.sofAssessment && r.sofAssessment.score, acc, { withReason: true }),
    narrative: (r.sofAssessment && r.sofAssessment.narrative) || '',
    namedAccount: (r.sofAssessment && r.sofAssessment.namedAccount) || '',
    fundingRoute: (r.sofAssessment && r.sofAssessment.fundingRoute) || '',
  };
  const activityChk = procedural.find((c) => c.key === 'activity') || {};
  const activity = {
    done: !!activityChk.done,
    resolved: !!activityChk.officerResolved,
    modelScore: (r.activityProfile && r.activityProfile.score) || null,
    displayScore: activityChk.officerResolved ? ('Accepted by the MLRO' + (activityChk.officerResolved.note ? ': ' + activityChk.officerResolved.note : '')) : ((r.activityProfile && r.activityProfile.score) || activityDetail),
    resolution: activityChk.officerResolved || null,
  };
  // v1_748: resolved-state views the flow map reads so a resolved check shows the
  // officer's resolution instead of amber.
  const _interChk = [...checks, ...procedural].find((c) => c.key === 'intermediary') || {};
  const _sowChk = procedural.find((c) => c.key === 'sow') || {};
  const intermediary = {
    required: interReq,
    done: !!_interChk.done,
    resolution: _interChk.officerResolved || null,
    screenings: intermediaryScreening,
    // Per-intermediate view for the flow map: verified by evidence, or cleared by
    // the officer resolution on the whole check.
    verifiedName: (name) => !!(_interChk.officerResolved) || intermediaryScreening.some((x) => nm2(x.name) === nm2(name) && x.verified),
  };
  const sowInterview = {
    done: !!_sowChk.done,
    resolution: _sowChk.officerResolved || null,
    flagged: sowFlagged.map((u) => u.name),
    defaultResolveReason: sowDefaultResolveReason,
  };

  return {
    applicant: clean(r.clientName) || clean(r.companyDetails && r.companyDetails.legalName) || clean(session.companyName),
    checks, procedural, exceptions,
    reconciledOutstanding, noted, openForOverride, openItems, openItemsCount: openItems.length,
    documentGaps, materialDocs, notReadDocs,
    sow, sof, activity,
    amlPolicy: { required: amlReq, done: amlDone, displayScore: amlDetail },
    screening: { entity, parties, rowState: screeningRow },
    ownership,
    intermediaryScreening, intermediary, sowInterview, sowDefaultResolveReason,
    verificationParties: vParties, partyCount, verifiedCount,
    officerResolutions,
    readyForSignoff, decisionRecord,
  };
}
